/**
 * « Ajouter des SKILLCARDS » (tableau de bord élève / joueur, page « Mes SKILLCARDS ») :
 * ouvre le sélecteur INTERNE (`SkillcardCatalogModal`) sur les packs du niveau choisi
 * (celui de l'élève par défaut), filtrables par matière ; choisir une carte ouvre sa
 * page dans l'app (exercices, progression, déblocage). Plus de renvoi vers le site.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { useCurriculumTimeline, type TimelinePackRow } from '@/lib/curriculum/api';
import { useStudentSkillcards } from '@/lib/cards/student-skillcards';
import {
  SkillcardCatalogModal,
  recentCatalogCards,
  type CatalogSection,
} from './skillcard-catalog-modal';

const DEFAULT_LEVEL = '3e';

/** Sections du sélecteur : un pack = une section, filtrées par matière ; sans doublon. PUR. */
export function browserSections(
  packs: readonly Pick<TimelinePackRow, 'id' | 'title' | 'subject' | 'cards'>[],
  subject: string,
): CatalogSection[] {
  const seen = new Set<string>();
  const out: CatalogSection[] = [];
  for (const p of packs) {
    if (subject && p.subject !== subject) continue;
    const cards = p.cards
      .filter((c) => !seen.has(c.id))
      .map((c) => {
        seen.add(c.id);
        return { id: c.id, title: c.title, rarity: c.rarity ?? null };
      });
    if (cards.length > 0) out.push({ key: p.id, label: p.title, cards });
  }
  return out;
}

function useMyLevel(userId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['student-profile-level', userId],
    enabled: enabled && !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from('student_profiles')
        .select('level')
        .eq('id', userId!)
        .maybeSingle();
      return (data?.level as string | null) ?? null;
    },
  });
}

export function SkillcardBrowserModal({
  userId,
  level: givenLevel,
  onClose,
}: {
  userId: string | null;
  level?: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const myLevel = useMyLevel(userId, givenLevel === undefined);
  const initial = givenLevel ?? myLevel.data ?? null;
  const [picked, setPicked] = useState<string | null>(null);
  const level = picked ?? initial ?? DEFAULT_LEVEL;
  const [subject, setSubject] = useState('');
  const packs = useCurriculumTimeline(level, null);
  const mine = useStudentSkillcards(userId);

  const sections = useMemo(() => browserSections(packs.data ?? [], subject), [packs.data, subject]);
  const chips = useMemo(() => {
    const present = new Set((packs.data ?? []).map((p) => p.subject));
    return SUBJECTS.filter((s) => present.has(s.value)).map((s) => ({
      value: s.value,
      label: s.label,
    }));
  }, [packs.data]);
  // ses dernières cartes, quel que soit le niveau parcouru
  const recent = useMemo(() => recentCatalogCards(mine.data ?? [], null), [mine.data]);

  return (
    <SkillcardCatalogModal
      title="Ajouter des SKILLCARDS"
      sections={sections}
      recent={recent}
      selected={[]}
      onToggle={(cardId) => {
        onClose();
        void navigate({ to: '/card/$cardId', params: { cardId } });
      }}
      onClose={onClose}
      chips={chips}
      chipValue={subject}
      onChip={setSubject}
      allChipsLabel="Toutes les matières"
      loading={packs.isLoading}
      emptyLabel="Aucune SKILLCARD publiée pour ce niveau."
      header={
        <label className="flex items-center gap-2 px-3 pb-1 pt-2">
          <span className="text-ink-700 text-sm font-semibold">Niveau</span>
          <select
            value={level}
            onChange={(e) => {
              setPicked(e.target.value);
              setSubject('');
            }}
            className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 flex-1 rounded-xl border px-3 text-sm focus:outline-none"
          >
            {SCHOOL_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      }
    />
  );
}

export function AddSkillcardsButton({
  userId,
  level,
}: {
  userId: string | null;
  /** Niveau de l'élève s'il est déjà connu (sinon lu sur son profil). */
  level?: string | null;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="border-primary-200 text-primary-600 hover:bg-primary-50 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed text-sm font-semibold active:scale-[0.98]"
      >
        <Plus className="h-4 w-4" aria-hidden /> Ajouter des SKILLCARDS
      </button>
      {open && (
        <SkillcardBrowserModal userId={userId} level={level} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
